import { cn } from "@newxsystems/shared";
import type { CSSProperties } from "react";
import { resolveTootPrimitive } from "../../../../client";
import type { ITootPersonCardProps, TootPersonLink } from "./TootPersonCard.props";

const getSafeRel = (target?: string, rel?: string) => {
  if (target !== "_blank") {
    return rel;
  }

  const tokens = new Set((rel ?? "").split(/\s+/).filter(Boolean));
  tokens.add("noopener");
  tokens.add("noreferrer");
  return Array.from(tokens).join(" ");
};

const renderLinks = (links: TootPersonLink[] | undefined, tone: "contact" | "social") => {
  if (!links?.length) {
    return null;
  }

  const Link = resolveTootPrimitive("Link");

  return (
    <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
      {links.map((link) => (
        <li key={`${link.label}-${link.href}`}>
          <Link
            aria-label={link.ariaLabel ?? link.label}
            className={cn(
              "inline-flex min-h-11 items-center rounded-full border px-3.5 py-2 text-[0.7rem] font-black uppercase tracking-[0.14em] transition-[background-color,border-color,color,transform] duration-200 ease-out motion-reduce:transition-none",
              tone === "contact"
                ? "border-[color-mix(in_oklab,var(--color-toot-red)_58%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-red)_14%,transparent)] text-primary-700 hover:-translate-y-0.5 hover:border-[var(--color-toot-red)] hover:bg-[var(--color-toot-red)] hover:text-[var(--color-primary-foreground)] motion-reduce:hover:translate-y-0"
                : "border-[color-mix(in_oklab,var(--color-toot-orange)_44%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-orange)_10%,transparent)] text-toot-orange hover:-translate-y-0.5 hover:border-[var(--color-toot-orange)] hover:bg-[color-mix(in_oklab,var(--color-toot-orange)_22%,transparent)] motion-reduce:hover:translate-y-0",
            )}
            href={link.href}
            rel={getSafeRel(link.target, link.rel)}
            target={link.target}
          >
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  );
};

export const TootPersonCard = ({
  bio,
  className,
  contactLinks,
  department,
  featured = false,
  image,
  name,
  role,
  socialLinks,
}: ITootPersonCardProps) => {
  const Image = resolveTootPrimitive("Image");
  const imageSrc = image?.defaultSrc;
  const hasImage = Boolean(imageSrc);
  const imageStyle: CSSProperties | undefined = image?.focalPoint
    ? { objectPosition: `${image.focalPoint.x * 100}% ${image.focalPoint.y * 100}%` }
    : undefined;
  const statusLabel = department;
  const contact = renderLinks(contactLinks, "contact");
  const socials = renderLinks(socialLinks, "social");

  return (
    <article
      className={cn(
        "group/person relative isolate h-full min-w-0 overflow-hidden rounded-[var(--radius-toot-frame)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_78%,transparent)] bg-[radial-gradient(circle_at_100%_0%,rgba(255,77,46,0.1),transparent_15rem),linear-gradient(180deg,var(--color-toot-surface-raised),var(--color-toot-surface-terminal)_74%,var(--color-toot-black))] text-[var(--color-toot-foreground)] shadow-[0_1.25rem_3.5rem_rgba(0,0,0,0.32)] transition-[border-color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:border-[color-mix(in_oklab,var(--color-toot-orange)_58%,var(--color-toot-grid-strong))] hover:shadow-[0_1.5rem_4.5rem_rgba(0,0,0,0.44),0_0_0_1px_rgba(255,77,46,0.12)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        featured && "xl:grid xl:grid-cols-[minmax(14rem,0.8fr)_minmax(0,1fr)]",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,77,46,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.055)_1px,transparent_1px)] bg-[size:1.75rem_1.75rem] opacity-38 [mask-image:linear-gradient(180deg,black,transparent_92%)]"
      />

      {hasImage ? (
        <div className={cn("relative p-3.5 pb-0", featured && "xl:p-4 xl:pr-0")}>
          <div
            className={cn(
              "relative overflow-hidden rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_30%,transparent)] bg-[var(--color-toot-black)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]",
              featured && "xl:h-full",
            )}
          >
            <Image
              alt={image?.alternateText ?? ""}
              className={cn(
                "aspect-[5/4] w-full object-cover object-center brightness-[0.78] contrast-[1.18] saturate-[0.86] transition-[filter,transform] duration-500 ease-out group-hover/person:scale-[1.025] group-hover/person:brightness-[0.9] group-hover/person:saturate-100 motion-reduce:transition-none motion-reduce:group-hover/person:scale-100",
                featured && "xl:aspect-auto xl:h-full xl:min-h-full",
              )}
              height={image?.height ?? undefined}
              sizes={image?.sizes ?? undefined}
              src={imageSrc ?? ""}
              srcSet={image?.srcSet ?? undefined}
              style={imageStyle}
              width={image?.width ?? undefined}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_75%_12%,rgba(255,77,46,0.3),transparent_30%),linear-gradient(180deg,rgba(5,5,6,0.02),rgba(5,5,6,0.68))]"
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.08)_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-28 mix-blend-screen"
            />
            {statusLabel ? (
              <div className="absolute inset-x-3 bottom-3 flex items-center justify-end gap-3 text-[0.62rem] font-black uppercase tracking-[0.16em]">
                <span className="min-w-0 truncate rounded-[var(--radius-toot-panel)] border border-toot-grid/90 bg-toot-surface-raised/80 px-2.5 py-1 text-[color-mix(in_oklab,var(--color-toot-foreground)_72%,transparent)]">
                  {statusLabel}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className={cn("relative flex min-w-0 flex-1 flex-col gap-4 p-5", featured && "xl:p-6")}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {role ? (
              <p className="text-[0.72rem] font-black uppercase tracking-[0.16em] text-[var(--color-toot-orange)] [overflow-wrap:anywhere]">
                {role}
              </p>
            ) : null}
            <h3 className="mt-2 text-balance text-2xl font-black leading-[1.02] tracking-[-0.045em] text-toot-foreground md:text-[1.7rem]">
              {name}
            </h3>
          </div>
        </div>

        {bio ? (
          <p className="max-w-[58ch] text-sm leading-6 text-[color-mix(in_oklab,var(--color-toot-foreground)_76%,transparent)] sm:text-[0.95rem] sm:leading-7">
            {bio}
          </p>
        ) : null}

        {contact || socials ? (
          <div className="mt-auto flex flex-col gap-2.5 pt-1">
            {contact}
            {socials}
          </div>
        ) : null}
      </div>
    </article>
  );
};

TootPersonCard.displayName = "People::TootPersonCard";
